import {
  createCoordinateTransformConfig,
  applyJointValueTransformation,
} from "../utils/coordinateTransform";
import { logger } from "../utils/logger";

/**
 * General-purpose keyframe handler for MuJoCo XML files
 * Handles different robot configurations and qpos structures automatically
 */

/**
 * Analyze the qpos structure to determine how to map values to joints
 * @param {Array} qpos - The qpos array from the keyframe
 * @param {Array} joints - The joints array from the robot model
 * @returns {Object} Configuration for mapping qpos to joints
 */
export function analyzeQposStructure(qpos, joints) {
  logger.debug("[KeyframeHandler] Analyzing qpos structure:", {
    qposLength: qpos.length,
    jointsLength: joints.length,
  });

  // Common patterns for different robot types
  const patterns = {
    // Free-floating base (7 values: 3 pos + 4 quat) + joints
    freeFloating: {
      baseValues: 7,
      description: "Free-floating base (position + orientation) + joints",
    },
    // Fixed base (0 values) + joints only
    fixedBase: {
      baseValues: 0,
      description: "Fixed base, joints only",
    },
    // Position-only base (3 values: x, y, z) + joints
    positionOnly: {
      baseValues: 3,
      description: "Position-only base + joints",
    },
  };

  // Determine the pattern based on qpos length and joint count
  let pattern;
  let baseValues;
  /** When set, `jointValues` maps to these joints in order (MuJoCo qpos tail); excludes free/ball. */
  let mappingJoints = undefined;
  let ignoredTailValues = [];
  let usesJointQposCursor = false;

  const scalarJoints = (joints || []).filter(
    (j) => j && (j.kind === "hinge" || j.kind === "slide")
  );
  const freeJointCount = (joints || []).filter((j) => j && j.kind === "free").length;
  const ballJointCount = (joints || []).filter((j) => j && j.kind === "ball").length;
  const qposWidthForJoint = (joint) => {
    if (!joint) return 0;
    if (joint.kind === "free") return 7;
    if (joint.kind === "ball") return 4;
    if (joint.kind === "hinge" || joint.kind === "slide") return 1;
    return 0;
  };
  const jointQposWidth = (joints || []).reduce((sum, joint) => sum + qposWidthForJoint(joint), 0);

  if (
    jointQposWidth > 0 &&
    qpos.length === jointQposWidth &&
    (scalarJoints.length > 0 || ballJointCount > 0) &&
    (freeJointCount > 0 || ballJointCount > 0)
  ) {
    // Exact MuJoCo joint-order qpos layout. Free and ball joints can appear anywhere in
    // body-tree order, so a fixed "base qpos then scalar tail" split would shift later joints.
    pattern = patterns.fixedBase;
    baseValues = 0;
    usesJointQposCursor = true;
  } else if (qpos.length === joints.length) {
    // Exact match - fixed base robot
    pattern = patterns.fixedBase;
    baseValues = 0;
  } else if (joints.length === 1 && joints[0].kind === "free" && qpos.length === 7) {
    // Standalone free-floating body (e.g. object library); qpos is only the free joint pose
    pattern = patterns.freeFloating;
    baseValues = 7;
  } else if (
    freeJointCount === 1 &&
    ballJointCount === 0 &&
    scalarJoints.length > 0 &&
    qpos.length === 7 + scalarJoints.length
  ) {
    // Free base + hinges/slides: the free joint is one list entry but consumes 7 qpos values
    // (do not use qpos.length === joints.length + 7 — that is off by one when `joints` includes the free joint).
    pattern = patterns.freeFloating;
    baseValues = 7;
    mappingJoints = scalarJoints;
  } else if (
    freeJointCount === 1 &&
    ballJointCount === 0 &&
    scalarJoints.length > 0 &&
    qpos.length === 7 + scalarJoints.length + 1
  ) {
    // Some source MJCFs include one surplus scalar after the free root pose. The
    // tail still belongs to hinge/slide joints only; mapping it to the freejoint
    // shifts every scalar joint and produces invalid poses.
    pattern = patterns.freeFloating;
    baseValues = 7;
    mappingJoints = scalarJoints;
    ignoredTailValues = qpos.slice(7 + scalarJoints.length);
    logger.warn(
      `[KeyframeHandler] Ignoring ${ignoredTailValues.length} qpos value(s) for joint(s) outside the selected body scope:`,
      ignoredTailValues
    );
  } else if (
    freeJointCount === 1 &&
    ballJointCount > 0 &&
    scalarJoints.length > 0 &&
    qpos.length === 7 + ballJointCount * 4 + scalarJoints.length
  ) {
    // Free base + ball joints + hinges/slides (e.g. Cassie).
    // Ball joints each consume 4 qpos values (quaternion); applyKeyframe uses pointer-based
    // traversal of the full joint list to skip them correctly.
    pattern = patterns.freeFloating;
    baseValues = 7;
    mappingJoints = scalarJoints;
  } else if (qpos.length === joints.length + 7) {
    // Free-floating base; joint list has no free joint (e.g. sceneLoader passes hinge/slide only)
    pattern = patterns.freeFloating;
    baseValues = 7;
  } else if (qpos.length === joints.length + 3) {
    // Position-only base robot
    pattern = patterns.positionOnly;
    baseValues = 3;
  } else {
    // Unknown pattern - try to infer
    const diff = qpos.length - joints.length;
    if (diff > 0 && diff <= 7) {
      baseValues = diff;
      pattern = {
        baseValues: diff,
        description: `Unknown base configuration (${diff} values) + joints`,
      };
    } else {
      logger.warn(
        `[KeyframeHandler] Unknown qpos structure: ${qpos.length} qpos values for ${joints.length} joints`
      );
      // Default to fixed base
      pattern = patterns.fixedBase;
      baseValues = 0;
    }
  }

  logger.debug(`[KeyframeHandler] Detected pattern: ${pattern.description}`);
  logger.debug(
    `[KeyframeHandler] Base values: ${baseValues}, Joint values: ${qpos.length - baseValues}`
  );

  return {
    pattern,
    baseValues,
    jointValues:
      ignoredTailValues.length > 0 && mappingJoints
        ? qpos.slice(baseValues, baseValues + mappingJoints.length)
        : qpos.slice(baseValues),
    totalJoints: joints.length,
    mappingJoints,
    hasBallJoints: ballJointCount > 0,
    usesJointQposCursor,
    ignoredTailValues,
  };
}

/**
 * Detect if coordinate system transformation is needed
 * @param {Array} bodies - The robot body tree (for base transformation info)
 * @param {Array} joints - The joints array
 * @param {Array} jointValues - The joint values
 * @returns {Object} Transformation configuration
 */
function detectCoordinateSystemTransformation(bodies, joints, jointValues) {
  // Use the generalized coordinate transformation system
  const transformConfig = createCoordinateTransformConfig(bodies, joints);

  const transformation = {
    needed: false,
    type: null,
    description: null,
    transformations: {},
  };

  if (!transformConfig.needsTransform) {
    return transformation;
  }

  transformation.needed = true;
  transformation.type = "coordinate_system";
  transformation.description = `Detected base rotation, applying generalized coordinate transformation`;

  logger.debug(`[KeyframeHandler] Base quaternion:`, transformConfig.baseQuat);
  logger.debug(`[KeyframeHandler] Original joint values:`, jointValues);

  // Apply transformations based on joint axis analysis
  joints.forEach((joint, index) => {
    if (index < jointValues.length) {
      const value = jointValues[index];
      const transformType = transformConfig.jointTransforms[joint.name] || "none";

      const transformedValue = applyJointValueTransformation(value, transformType);
      transformation.transformations[joint.name] = transformedValue;

      if (transformType !== "none") {
        logger.debug(
          `[KeyframeHandler] Transforming ${joint.name}: ${value} -> ${transformedValue} (${transformType})`
        );
      } else {
        logger.debug(
          `[KeyframeHandler] Keeping ${joint.name}: ${value} (no transformation needed)`
        );
      }
    }
  });

  logger.debug(
    `[KeyframeHandler] Transformed joint values:`,
    Object.values(transformation.transformations)
  );

  // Pattern 2: Check for joint range violations that might indicate coordinate system issues
  const rangeViolations = [];
  joints.forEach((joint, index) => {
    if (index < jointValues.length) {
      const value = jointValues[index];
      if (joint.range && (value < joint.range[0] || value > joint.range[1])) {
        rangeViolations.push({ joint: joint.name, value, range: joint.range });
      }
    }
  });

  // If many joints have range violations, try negating all values
  // But only if we haven't already applied a transformation
  if (rangeViolations.length > joints.length * 0.5 && !transformation.needed) {
    transformation.needed = true;
    transformation.type = "global_negation";
    transformation.description = "Detected widespread range violations, applying global negation";

    joints.forEach((joint, index) => {
      if (index < jointValues.length) {
        const value = jointValues[index];
        transformation.transformations[joint.name] = -value;
        logger.debug(
          `[KeyframeHandler] Negating ${joint.name}: ${value} -> ${-value} (range violation fix)`
        );
      }
    });
  }

  // Pattern 3: Check for unusually large values that might indicate unit conversion issues
  const hasLargeValues = jointValues.some((v) => Math.abs(v) > Math.PI);
  if (hasLargeValues && !transformation.needed) {
    // This might indicate degrees vs radians conversion
    transformation.needed = true;
    transformation.type = "unit_conversion";
    transformation.description = "Detected potential unit conversion (degrees to radians)";

    joints.forEach((joint, index) => {
      if (index < jointValues.length) {
        const value = jointValues[index];
        if (Math.abs(value) > Math.PI) {
          const convertedValue = (value * Math.PI) / 180;
          transformation.transformations[joint.name] = convertedValue;
          logger.debug(
            `[KeyframeHandler] Converting ${joint.name}: ${value}° -> ${convertedValue} rad`
          );
        } else {
          transformation.transformations[joint.name] = value;
        }
      }
    });
  }

  return transformation;
}

/**
 * Validate joint values against their ranges
 * @param {Array} joints - The joints array
 * @param {Array} jointValues - The joint values to validate
 * @returns {Object} Validation results
 */
function validateJointValues(joints, jointValues) {
  const validation = {
    valid: true,
    warnings: [],
    errors: [],
  };
  const isPartialKeyframe = jointValues.length > 0 && jointValues.length < joints.length;

  joints.forEach((joint, index) => {
    if (index < jointValues.length) {
      const value = jointValues[index];

      // Check if value is within joint range
      if (joint.range && (value < joint.range[0] || value > joint.range[1])) {
        validation.warnings.push({
          joint: joint.name,
          value,
          range: joint.range,
          message: `Joint "${joint.name}" value ${value} is outside range [${joint.range[0]}, ${joint.range[1]}]`,
        });
      }

      // Check for unusually large values (potential unit conversion issues)
      if (Math.abs(value) > 2 * Math.PI && joint.kind === "hinge") {
        validation.warnings.push({
          joint: joint.name,
          value,
          message: `Joint "${joint.name}" value ${value} seems unusually large for a rotational joint`,
        });
      }
    } else {
      if (isPartialKeyframe) {
        validation.warnings.push({
          joint: joint.name,
          message: `No qpos value for joint "${joint.name}" at index ${index}; leaving current value unchanged`,
        });
        return;
      }
      // Robot keyframes often omit free-joint objects that are merged into the scene later
      // (e.g. cube/tray in pick-and-place presets). Keep those joints at their current pose.
      if (joint.kind === "free") {
        validation.warnings.push({
          joint: joint.name,
          message: `No qpos value for free joint "${joint.name}" at index ${index}; leaving current value unchanged`,
        });
        return;
      }
      validation.errors.push({
        joint: joint.name,
        message: `No qpos value found for joint "${joint.name}" at index ${index}`,
      });
      validation.valid = false;
    }
  });

  return validation;
}

function orderJointsByBodyTree(bodies, joints) {
  if (!Array.isArray(bodies) || bodies.length === 0 || !Array.isArray(joints)) return joints;
  const jointsByUuid = new Map();
  joints.forEach((joint) => {
    if (joint?.uuid) jointsByUuid.set(String(joint.uuid), joint);
  });
  if (jointsByUuid.size === 0) return joints;

  const ordered = [];
  const seen = new Set();
  const visit = (body) => {
    if (!body) return;
    (body.joints || []).forEach((jointRef) => {
      const jointUuid = typeof jointRef === "string" ? jointRef : "";
      const joint = jointsByUuid.get(jointUuid);
      if (!joint || seen.has(jointUuid)) return;
      ordered.push(joint);
      seen.add(jointUuid);
    });
    (body.children || []).forEach((child) => visit(child));
  };

  bodies.forEach((body) => visit(body));
  if (ordered.length === 0) return joints;

  joints.forEach((joint) => {
    const jointUuid = joint?.uuid ? String(joint.uuid) : "";
    if (!jointUuid || seen.has(jointUuid)) return;
    ordered.push(joint);
    seen.add(jointUuid);
  });
  return ordered;
}

/**
 * Apply keyframe values to joints
 * @param {Array} qpos - The qpos array from the keyframe
 * @param {Array} joints - The joints array from the robot model
 * @param {Array} bodies - The robot body tree for coordinate transformation
 * @param {Function} updateJointValue - Function to update individual joint values
 * @returns {Object} Result of the keyframe application
 */
export function applyKeyframe(qpos, joints, bodies, updateJointValue, updateBallJointQuat = null) {
  const qposOrderedJoints = orderJointsByBodyTree(bodies, joints);
  logger.debug("[KeyframeHandler] Applying keyframe:", {
    qpos,
    jointsCount: qposOrderedJoints.length,
  });

  // Analyze the qpos structure
  const structure = analyzeQposStructure(qpos, qposOrderedJoints);

  // Extract joint values (skip base values)
  const jointValues = structure.jointValues;
  const jointsForTail = structure.mappingJoints || qposOrderedJoints;
  const extractScalarValuesFromJointQpos = () => {
    const extracted = [];
    let ptr = 0;
    qposOrderedJoints.forEach((joint) => {
      if (!joint) return;
      if (joint.kind === "free") {
        ptr += 7;
        return;
      }
      if (joint.kind === "ball") {
        ptr += 4;
        return;
      }
      if (joint.kind === "hinge" || joint.kind === "slide") {
        if (ptr < jointValues.length) extracted.push(jointValues[ptr]);
        ptr += 1;
      }
    });
    return extracted;
  };
  const scalarJointsForJointQpos = (qposOrderedJoints || []).filter(
    (joint) => joint && (joint.kind === "hinge" || joint.kind === "slide")
  );
  const transformationJoints = structure.usesJointQposCursor
    ? scalarJointsForJointQpos
    : jointsForTail;
  const transformationValues = structure.usesJointQposCursor
    ? extractScalarValuesFromJointQpos()
    : jointValues;

  // Detect if coordinate system transformation is needed
  const transformation = detectCoordinateSystemTransformation(
    bodies,
    transformationJoints,
    transformationValues
  );

  if (transformation.needed) {
    logger.debug(
      `[KeyframeHandler] Applying coordinate system transformation: ${transformation.description}`
    );
  }

  // Validate the joint values against scalar-only joints and values.
  // Ball joint quaternions are interspersed with scalar values (depth-first order), so we must
  // do the same pointer-based traversal as applyKeyframe rather than a simple prefix skip.
  let scalarValuesForValidation;
  let jointsForValidation = jointsForTail;
  if (structure.usesJointQposCursor) {
    scalarValuesForValidation = extractScalarValuesFromJointQpos();
    jointsForValidation = scalarJointsForJointQpos;
  } else if (structure.hasBallJoints) {
    const extracted = [];
    let ptr = 0;
    qposOrderedJoints.forEach((joint) => {
      if (!joint) return;
      if (joint.kind === "free") return;
      if (joint.kind === "ball") {
        ptr += 4;
      } else if (joint.kind === "hinge" || joint.kind === "slide") {
        if (ptr < jointValues.length) extracted.push(jointValues[ptr++]);
      }
    });
    scalarValuesForValidation = extracted;
    jointsForValidation = (jointsForTail || []).filter(
      (joint) => joint && (joint.kind === "hinge" || joint.kind === "slide")
    );
  } else {
    scalarValuesForValidation = jointValues;
  }
  const validation = validateJointValues(
    jointsForValidation,
    transformation.needed
      ? Object.values(transformation.transformations)
      : scalarValuesForValidation
  );

  // Log validation results
  if (validation.warnings.length > 0) {
    logger.warn("[KeyframeHandler] Validation warnings:", validation.warnings);
  }

  if (validation.errors.length > 0) {
    logger.error("[KeyframeHandler] Validation errors:", validation.errors);
    return {
      success: false,
      errors: validation.errors,
      warnings: validation.warnings,
    };
  }

  // Apply joint values
  const appliedValues = {};

  if (structure.usesJointQposCursor) {
    // Exact MuJoCo qpos traversal: walk all joints in body-tree order, advancing
    // by each joint's qpos width. Free joints are consumed but not updated here.
    let ptr = 0;
    qposOrderedJoints.forEach((joint) => {
      if (!joint) return;
      if (joint.kind === "free") {
        ptr += 7;
        return;
      }
      if (joint.kind === "ball") {
        if (ptr + 3 < jointValues.length && updateBallJointQuat) {
          const qw = jointValues[ptr];
          const qx = jointValues[ptr + 1];
          const qy = jointValues[ptr + 2];
          const qz = jointValues[ptr + 3];
          appliedValues[joint.name] = [qw, qx, qy, qz];
          logger.debug(
            `[KeyframeHandler] Setting ball joint "${joint.name}" quat [${qw}, ${qx}, ${qy}, ${qz}]`
          );
          updateBallJointQuat(joint.uuid, [qw, qx, qy, qz]);
        }
        ptr += 4;
        return;
      }
      if (joint.kind === "hinge" || joint.kind === "slide") {
        if (ptr < jointValues.length) {
          const value = transformation.needed
            ? transformation.transformations[joint.name]
            : jointValues[ptr];
          appliedValues[joint.name] = value;
          logger.debug(`[KeyframeHandler] Setting joint "${joint.name}" to value ${value}`);
          updateJointValue(joint.uuid, value);
        }
        ptr += 1;
      }
    });
  } else if (structure.hasBallJoints) {
    // Pointer-based traversal: walk all joints in tree order, advancing the qpos
    // pointer by 4 for each ball joint and by 1 for each scalar joint.
    let ptr = 0;
    qposOrderedJoints.forEach((joint) => {
      if (!joint) return;
      if (joint.kind === "free") return; // consumed in baseValues
      if (joint.kind === "ball") {
        if (ptr + 3 < jointValues.length && updateBallJointQuat) {
          const qw = jointValues[ptr];
          const qx = jointValues[ptr + 1];
          const qy = jointValues[ptr + 2];
          const qz = jointValues[ptr + 3];
          appliedValues[joint.name] = [qw, qx, qy, qz];
          logger.debug(
            `[KeyframeHandler] Setting ball joint "${joint.name}" quat [${qw}, ${qx}, ${qy}, ${qz}]`
          );
          updateBallJointQuat(joint.uuid, [qw, qx, qy, qz]);
        }
        ptr += 4;
      } else if (joint.kind === "hinge" || joint.kind === "slide") {
        if (ptr < jointValues.length) {
          const value = jointValues[ptr++];
          appliedValues[joint.name] = value;
          logger.debug(`[KeyframeHandler] Setting joint "${joint.name}" to value ${value}`);
          updateJointValue(joint.uuid, value);
        }
      }
    });
  } else {
    jointsForTail.forEach((joint, index) => {
      if (index < jointValues.length) {
        let value;

        if (transformation.needed) {
          value = transformation.transformations[joint.name];
        } else {
          value = jointValues[index];
        }

        appliedValues[joint.name] = value;

        logger.debug(`[KeyframeHandler] Setting joint "${joint.name}" to value ${value}`);

        // Update the joint value
        updateJointValue(joint.uuid, value);
      }
    });
  }

  logger.debug("[KeyframeHandler] Applied joint values:", appliedValues);

  return {
    success: true,
    appliedValues,
    warnings: validation.warnings,
    structure: structure.pattern.description,
    transformation: transformation.needed ? transformation.description : null,
  };
}

/**
 * Get keyframe information for debugging
 * @param {Array} qpos - The qpos array from the keyframe
 * @param {Array} joints - The joints array from the robot model
 * @param {Array} bodies - The robot body tree for coordinate transformation
 * @returns {Object} Keyframe analysis information
 */
export function analyzeKeyframe(qpos, joints, bodies) {
  const structure = analyzeQposStructure(qpos, joints);
  const jointsForTail = structure.mappingJoints || joints;
  const transformation = detectCoordinateSystemTransformation(
    bodies,
    jointsForTail,
    structure.jointValues
  );
  const validation = validateJointValues(
    jointsForTail,
    transformation.needed ? Object.values(transformation.transformations) : structure.jointValues
  );

  return {
    qposLength: qpos.length,
    jointsLength: joints.length,
    structure: structure.pattern,
    transformation,
    validation,
    jointValues: structure.jointValues,
    baseValues: qpos.slice(0, structure.baseValues),
  };
}
